/**
 * A folder picker on a menu: a few fixed choices (All Documents and
 * Unfiled for the library's narrow toolbar, Top Level for an upload's Add
 * To), then the firm's folders as an indented tree.
 */
import {
  Check,
  ChevronDown,
  Folder,
  FolderOpen,
  type LucideIcon
} from "lucide-react";
import type { FC } from "react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

import { folderPathLabel, folderRows, type FolderTree } from "../folders";

export type FolderSelectChoice = {
  value: string;
  label: string;
  icon: LucideIcon;
};

type Props = {
  tree: FolderTree;
  /** A fixed choice's value or a folder id. */
  value: string;
  onChange: (value: string) => void;
  choices: FolderSelectChoice[];
  ariaLabel: string;
  className?: string;
  disabled?: boolean;
  "data-testid"?: string;
};

export const FolderSelect: FC<Props> = ({
  tree,
  value,
  onChange,
  choices,
  ariaLabel,
  className,
  disabled,
  "data-testid": testId
}) => {
  const fixed = choices.find((c) => c.value === value);
  const Icon = fixed?.icon ?? FolderOpen;
  const rows = folderRows(tree, "all");
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="secondary"
          size="sm"
          className={cn("min-w-0 justify-start", className)}
          aria-label={ariaLabel}
          disabled={disabled}
          data-testid={testId}
        >
          <Icon className="text-fg-3" />
          <span className="min-w-0 flex-1 truncate text-left">
            {fixed?.label ?? folderPathLabel(tree, value)}
          </span>
          <ChevronDown className="text-fg-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-auto max-w-90 min-w-60">
        {choices.map((choice) => (
          <DropdownMenuItem
            key={choice.value}
            onSelect={() => onChange(choice.value)}
          >
            <choice.icon className="text-fg-3" />
            <span className="min-w-0 flex-1 truncate">{choice.label}</span>
            {value === choice.value ? (
              <Check className="ml-auto size-3.5 text-accent-seed" />
            ) : null}
          </DropdownMenuItem>
        ))}
        {rows.length ? <DropdownMenuSeparator /> : null}
        <div className="max-h-70 min-w-0 overflow-y-auto">
          {rows.map(({ folder, depth }) => (
            <DropdownMenuItem
              key={folder.id}
              onSelect={() => onChange(folder.id)}
              style={{ paddingLeft: 8 + depth * 14 }}
            >
              <Folder className="text-fg-3" />
              <span className="min-w-0 flex-1 truncate">{folder.name}</span>
              {value === folder.id ? (
                <Check className="ml-auto size-3.5 text-accent-seed" />
              ) : null}
            </DropdownMenuItem>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
